import React from "react";

import { isDefined } from "@utils/helpers";
import { useWoosmapServices } from "@utils/hooks/useWoosmapServices.hook";

import {
    broadcastMessage,
    clearMessageById,
} from "@redux/modules/value/messages/messages.utils";
import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { StoreSearchInput } from "@components/Store/StoreSearchInput/storeSearchInput.container";
import {
    SearchItem,
    SearchItemType,
} from "@components/Store/StoreSearchInput/storeSearchInput.types";

import {
    STORE_DIRECTIONS_MESSAGE_ID,
    STORE_SEARCH_INPUT_TYPES,
} from "../../storeDirections.config";
import { useStoreDirectionsContext } from "../../storeDirections.context";
import {
    fetchLocalityDetails,
    fetchLocalityGeocode,
} from "./storeDirectionsInput.helpers";

const StoreDirectionsInput = () => {
    const { t } = useGranite();

    const { origin, setOrigin, resetOrigin } = useStoreDirectionsContext();

    const { LocalitiesService } = useWoosmapServices();

    const handleOnUseLocation = (latitude: number, longitude: number) => {
        clearMessageById(STORE_DIRECTIONS_MESSAGE_ID);

        fetchLocalityGeocode(LocalitiesService, {
            lat: latitude,
            lng: longitude,
        })
            .then(result => ({
                value: result.public_id,
                latitude: result.geometry?.location.lat,
                longitude: result.geometry?.location.lng,
                label: t("form.storeDirections.myLocation"),
                type: SearchItemType.Locality,
            }))
            .then(setOrigin)
            .catch(handleOnLocalityNotFoundError);
    };

    const handleOnSelect = (searchItem: SearchItem | null) => {
        clearMessageById(STORE_DIRECTIONS_MESSAGE_ID);

        if (!isDefined(searchItem)) return;

        fetchLocalityDetails(LocalitiesService, searchItem.value)
            .then(result => ({
                ...searchItem,
                latitude: result.geometry?.location.lat,
                longitude: result.geometry?.location.lng,
            }))
            .then(setOrigin)
            .catch(handleOnLocalityNotFoundError);
    };

    const handleOnLocalityNotFoundError = () =>
        broadcastMessage({
            id: STORE_DIRECTIONS_MESSAGE_ID,
            text: t("storesearch.storedetails.notFound"),
            status: "danger",
            dismissible: true,
        });

    return (
        <StoreSearchInput
            messageId={STORE_DIRECTIONS_MESSAGE_ID}
            value={origin}
            itemsToRetrieve={STORE_SEARCH_INPUT_TYPES}
            placeholderText={t("form.storeDirections.input.placeholder")}
            loadingText={t("form.storeDirections.loading")}
            noResultsText={t("form.storeDirections.nothingFound")}
            buttonContent={<>{t("form.storeDirections.showRoute")}</>}
            aria-label={t("accessibility.storeDirections.input.label")}
            clearValue={resetOrigin}
            onSelect={handleOnSelect}
            onUseLocation={handleOnUseLocation}
        />
    );
};

export { StoreDirectionsInput };
